import { useAtomValue, useSetAtom } from "jotai";
import { useCallback, useMemo, useRef } from "react";

import {
	$spawnTerminal,
	$terminalState,
	type TerminalHandle,
	type TerminalSpawnOptions,
} from "../../internal/terminal";

export type UseTerminalParams = {
	documentId: string;
	terminalId?: string;
};

export const useTerminal = ({ documentId, terminalId }: UseTerminalParams) => {
	const key = useMemo(() => ({ documentId, terminalId }), [documentId, terminalId]);
	const handleRef = useRef<TerminalHandle | null>(null);

	const state = useAtomValue($terminalState(key));
	const spawnTerminalAtom = useSetAtom($spawnTerminal);

	const spawn = useCallback(
		async (options?: TerminalSpawnOptions): Promise<TerminalHandle | null> => {
			const handle = await spawnTerminalAtom({ key, options });
			handleRef.current = handle;
			return handle;
		},
		[spawnTerminalAtom, key]
	);

	const getHandle = useCallback(() => handleRef.current, []);

	return {
		state,
		spawn,
		getHandle,
		isRunning: state.status === "running",
		isIdle: state.status === "idle",
		isExited: state.status === "exited",
	};
};
